import { AsyncPipe, NgClass } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    ViewEncapsulation
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { Observable } from "rxjs";
import {
    filter,
    map,
    shareReplay,
    startWith,
    withLatestFrom
} from "rxjs/operators";
import { SenastAnvandaPrislistorFilterValues } from "~app/common/models/constants";
import { PristabellRad, RowHeaderAction } from "~app/features/virkeskop/models/pristabell/pristabell-models";
import { AvtalMediatorService } from "~app/features/virkeskop/services/avtal-mediator.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { UtskriftMediatorService } from "~app/features/virkeskop/services/utskrift-mediator.service";
import { UtskriftWebinstallningarService } from "~app/features/virkeskop/services/utskrift-webinstallningar.service";
import { canEdit } from "~app/role-permissions";
import { DisabledControlDirective } from "../../../../../common/directives/disabled-control/disabled-control.directive";
import { TableKeyboardNavigationDirective } from "../../../../../common/directives/table-keyboard-navigation/table-keyboard-navigation.directive";
import { VirkeskopValidationMessageComponent } from "../../../validation/validation-message/virkeskop-validation-message.component";
import { KontraktPrislistaFilterComponent } from "../kontrakt-prislista-filter/kontrakt-prislista-filter.component";
import { KontraktPrisradComponent } from "../kontrakt-prisrad/kontrakt-prisrad.component";
import { KontraktUtskriftForm } from "../kontraktUtskriftForm";
const prislistaFilterTypes = [
    SenastAnvandaPrislistorFilterValues.VLG,
    SenastAnvandaPrislistorFilterValues.IckeVLG,
    SenastAnvandaPrislistorFilterValues.SenastAnvanda
];

@Component({
    selector: "rask-kontrakt-pristabell",
    templateUrl: "./kontrakt-pristabell.component.html",
    styleUrls: ["./kontrakt-pristabell.component.scss"],
    encapsulation: ViewEncapsulation.None,
    providers: [UtskriftWebinstallningarService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        VirkeskopValidationMessageComponent,
        RadiobuttonGroupComponent,
        FormsModule,
        ReactiveFormsModule,
        DisabledControlDirective,
        TableKeyboardNavigationDirective,
        KontraktPrislistaFilterComponent,
        KontraktPrisradComponent,
        NgClass,
        ButtonComponent,
        AsyncPipe
    ]
})
export class KontraktPristabellComponent {
    protected form = input<FormGroup<KontraktUtskriftForm>>();
    protected readonly validationRules = [
        "pristabellUnikaRader",
        "pristabellUnikaRaderViol3",
        "bruttoPrislista"
    ];

    protected isReadonly$: Observable<boolean>;
    protected prislistaFilter$: Observable<string>;
    protected prislistaFilterControl: FormControl<string>;
    protected prislistaFilterOptions = prislistaFilterTypes.map(x => ({
        key: x,
        value: x
    }));

    protected readonly pristabell = computed(() => this.utskrift().pristabell);

    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly utskriftMediatorService = inject(UtskriftMediatorService);
    private readonly utskriftWebinstallningarService = inject(UtskriftWebinstallningarService);

    private readonly utskrift = toSignal(this.utskriftMediatorService.utskriftValues$);

    protected get isU6() {
        return this.kontraktMediatorService.kontrakt.isAffarsformU6;
    }

    protected get useAPris() {
        return this.kontraktMediatorService.kontrakt.isAffarsformU4 || this.kontraktMediatorService.kontrakt.isAffarsformU5;
    }

    protected get kontraktGuid() {
        return this.kontraktMediatorService.kontrakt.guid;
    }

    protected get showPrislistaRadioButtons() {
        return this.viol2 && !this.isU6;
    }

    protected get viol2() {
        return !this.viol3;
    }

    protected get viol3() {
        return this.kontraktMediatorService.kontrakt.viol3;
    }

    private readonly canEdit = canEdit();

    constructor() {
        this.prislistaFilterControl = new FormControl<string>(SenastAnvandaPrislistorFilterValues.SenastAnvanda);

        this.prislistaFilter$ = this.prislistaFilterControl.valueChanges.pipe(
            startWith(this.prislistaFilterControl.value),
            shareReplay({
                refCount: false,
                bufferSize: 1
            }));

        this.avtalMediatorService.activeKontrakt$.pipe(
            takeUntilDestroyed(),
            filter(x => x.guid !== this.kontraktGuid)
        ).subscribe(kontrakt => {
            this.utskriftWebinstallningarService.mergeUtskriftInstallningar(this.utskrift());
        });

        this.avtalMediatorService.save$.pipe(
            takeUntilDestroyed(),
            withLatestFrom(this.avtalMediatorService.activeKontrakt$),
            filter(([
                __,
                kontrakt
            ]) => kontrakt.guid === this.kontraktGuid)
        ).subscribe(([
            __,
            kontrakt
        ]) => {
            this.utskriftWebinstallningarService.mergeUtskriftInstallningar(this.utskrift());
            this.utskriftWebinstallningarService.saveUtskriftInstallningar();
        });

        this.utskriftMediatorService.reset$.pipe(
            takeUntilDestroyed()
        ).subscribe(() => {
            this.prislistaFilterControl.setValue(
                SenastAnvandaPrislistorFilterValues.SenastAnvanda,
                {
                    onlySelf: false,
                    emitEvent: true
                });
        });

        this.isReadonly$ = this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit())
        );
    }

    protected onRowAction(action: RowHeaderAction, row: PristabellRad, index: number) {
        switch (action) {
            case "copy":
            {
                const copied = row.copy();
                this.utskriftMediatorService.addPrisrad(copied);
                break;
            }
            case "remove":
                if (index !== -1) {
                    this.removePrisrad(index);
                }
                break;
        }
    }

    protected onUpdate() {
        this.utskriftMediatorService.populatePristabell(this.pristabell());
    }

    protected pristabellFormArray() {
        return this.form()?.controls.pristabell;
    }

    private removePrisrad(index: number) {
        this.pristabellFormArray().removeAt(index);
        this.utskriftMediatorService.removePrisrad(this.pristabell()[index]);
    }
}
